import { useEffect, useMemo, useState } from "react";
import { Landmark, Play, Plus, Search, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { useEscape } from "@/lib/useEscape";
import { Pagination, usePagination } from "@/components/Pagination";

const STATUS_LABEL = { PENDING: "Tertunda", ACTIVE: "Aktif", DISPOSED: "Dijual/Dilepas" };
const TABS = [
  { key: "PENDING", label: "Aset Tertunda" },
  { key: "ACTIVE", label: "Aset Aktif" },
  { key: "DISPOSED", label: "Dijual/Dilepas" },
  { key: "DEPRECIATION", label: "Penyusutan" },
];
const fmtDate = (d) => (d ? d.split("-").reverse().join("-") : "—");
const fmtPeriod = (p) => (p ? p.split("-").reverse().join("/") : "—");

export default function Assets() {
  const [rows, setRows] = useState([]);
  const [deps, setDeps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ACTIVE");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [disposing, setDisposing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState(null);
  const amap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.code, a.name])), [accounts]);
  const load = () => Promise.all([api("/assets"), api("/assets/summary"), api("/assets/depreciations")])
    .then(([r, s, d]) => { setRows(r); setSummary(s); setDeps(d); })
    .catch((e) => setMessage({ text: e.message, type: "error" }));
  useEffect(() => { load(); api("/accounts").then(setAccounts).catch(() => {}); }, []);
  const filtered = rows.filter((r) => r.status === tab && [`(${r.number})`, r.name, r.asset_account, amap[r.asset_account], r.tags].join(" ").toLowerCase().includes(search.toLowerCase()));
  const depsFiltered = deps.filter((d) => [`(${d.asset_number})`, d.asset_name, d.period, d.journal_number].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(tab === "DEPRECIATION" ? depsFiltered : filtered, 25, `${tab}|${search}`);
  const runDepreciation = async () => {
    setRunning(true);
    try {
      const r = await api("/assets/run-depreciation", { method: "POST" });
      setMessage(r.months_posted > 0
        ? { text: `Penyusutan berhasil diposting: ${r.months_posted} periode, total ${money(r.total)}`, type: "success" }
        : { text: "Tidak ada penyusutan tertunda — semua sudah terposting sampai bulan ini", type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setRunning(false);
  };
  const remove = async () => {
    try {
      await api(`/assets/${deleting.id}`, { method: "DELETE" });
      setSelected(null);
      setMessage({ text: `Aset (${deleting.number}) ${deleting.name} berhasil dihapus`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  const activate = async (a) => {
    try {
      const r = await api(`/assets/${a.id}/activate`, { method: "POST" });
      setSelected(r); setTab("ACTIVE");
      setMessage({ text: `Aset (${r.number}) ${r.name} diaktifkan — jurnal perolehan diposting`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">MANAJEMEN ASET</p><h1>Aset Tetap</h1><p className="muted">Catat perolehan aset, penyusutan otomatis garis lurus, hingga penjualan/pelepasan.</p></div>
        <div className="asset-head-actions">
          {summary?.pending_depreciation_entries > 0 && (
            <button data-testid="asset-run-depreciation" className="secondary-btn" disabled={running} onClick={runDepreciation}>
              <Play size={14} /> {running ? "Memproses..." : `Jalankan Penyusutan (${summary.pending_depreciation_entries})`}
            </button>
          )}
          <button data-testid="asset-create-button" className="primary-btn" onClick={() => setShowForm(true)}><Plus size={16} /> Simpan Aset</button>
        </div>
      </div>
      {message && <div data-testid={message.type === "error" ? "asset-error-message" : "asset-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="report-tabs asset-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`asset-tab-${t.key.toLowerCase()}`} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
            {t.label} ({t.key === "DEPRECIATION" ? deps.length : summary?.[t.key] || 0})
          </button>
        ))}
        <div className="workflow-search asset-search"><Search size={16} /><input data-testid="asset-search-input" placeholder="Cari aset..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
      </div>
      <div className="surface workflow-table-wrap">
        {tab !== "DEPRECIATION" ? (
          <>
            <table className="workflow-table" data-testid="asset-table">
              <thead><tr><th>TANGGAL AKUISISI</th><th>DETAIL ASET</th><th>AKUN ASET</th><th className="right">BIAYA AKUISISI</th><th className="right">{tab === "DISPOSED" ? "LABA/RUGI" : "NILAI BUKU"}</th><th>AKSI</th></tr></thead>
              <tbody>
                {pager.pageItems.map((r) => (
                  <tr key={r.id} onClick={() => setSelected(r)} data-testid={`asset-row-${r.id}`}>
                    <td>{fmtDate(r.acquisition_date)}</td>
                    <td className="primary-cell">({r.number}) {r.name}</td>
                    <td>({r.asset_account}) {amap[r.asset_account] || ""}</td>
                    <td className="right mono">{money(r.acquisition_cost)}</td>
                    <td className={`right mono ${tab === "DISPOSED" ? (r.disposal_gain_loss < 0 ? "mv-out" : "mv-in") : ""}`}>
                      {tab === "DISPOSED" ? money(r.disposal_gain_loss || 0) : money(r.book_value)}
                    </td>
                    <td><span className="row-actions">
                      {r.status === "PENDING" && <button data-testid={`asset-activate-${r.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); activate(r); }}>Aktifkan</button>}
                      <button data-testid={`asset-open-${r.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setSelected(r); }}>Ubah</button>
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="empty-state" data-testid="asset-empty-state"><Landmark size={25} /><strong>Belum ada aset {STATUS_LABEL[tab]?.toLowerCase()}</strong><span>Klik "Simpan Aset" untuk mencatat aset tetap baru.</span></div>}
          </>
        ) : (
          <>
            <table className="workflow-table" data-testid="asset-dep-table">
              <thead><tr><th>PERIODE</th><th>ASET</th><th>NO. JURNAL</th><th className="right">JUMLAH PENYUSUTAN</th></tr></thead>
              <tbody>
                {pager.pageItems.map((d) => (
                  <tr key={d.id}>
                    <td>{fmtPeriod(d.period)}</td>
                    <td className="primary-cell">({d.asset_number}) {d.asset_name}</td>
                    <td className="mono">{d.journal_number}</td>
                    <td className="right mono">{money(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {depsFiltered.length === 0 && <div className="empty-state" data-testid="asset-dep-empty"><Landmark size={25} /><strong>Belum ada penyusutan terposting</strong><span>Klik "Jalankan Penyusutan" untuk memposting penyusutan bulanan yang tertunda.</span></div>}
          </>
        )}
        <Pagination pager={pager} testid="asset-pagination" />
      </div>
      {showForm && <AssetForm accounts={accounts} onClose={() => setShowForm(false)}
        onSaved={(r) => { setShowForm(false); setTab(r.status === "PENDING" ? "PENDING" : "ACTIVE"); load(); setMessage({ text: `Aset (${r.number}) ${r.name} berhasil disimpan${r.status === "PENDING" ? " sebagai tertunda (tanggal akuisisi di masa depan)" : " — jurnal perolehan diposting"}`, type: "success" }); }} />}
      {selected && <AssetDrawer asset={selected} amap={amap} deps={deps.filter((d) => d.asset_id === selected.id)}
        onClose={() => setSelected(null)}
        onActivate={() => activate(selected)}
        onDispose={() => setDisposing(selected)}
        onDelete={() => setDeleting(selected)}
        onRename={async (data) => {
          try {
            const r = await api(`/assets/${selected.id}`, { method: "PATCH", body: JSON.stringify(data) });
            setSelected(r); load();
            setMessage({ text: "Aset berhasil diperbarui", type: "success" });
          } catch (e) { setMessage({ text: e.message, type: "error" }); }
        }} />}
      {disposing && <DisposeModal asset={disposing} accounts={accounts} onClose={() => setDisposing(null)}
        onDone={(r) => { setDisposing(null); setSelected(null); setTab("DISPOSED"); load(); setMessage({ text: `Aset (${r.number}) ${r.name} dilepas — ${r.disposal_gain_loss >= 0 ? "laba" : "rugi"} ${money(Math.abs(r.disposal_gain_loss))} tercatat`, type: "success" }); }} />}
      {deleting && (
        <div className="modal-overlay" data-testid="asset-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head"><div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Aset?</h2></div><button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button></div>
              <p className="muted delete-warning">"({deleting.number}) {deleting.name}" akan dihapus dan jurnal perolehannya di-void. Aset yang sudah memiliki penyusutan harus dilepas lewat Jual/Lepas.</p>
              <div className="form-actions">
                <button data-testid="asset-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="asset-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetForm({ accounts, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [nextNumber, setNextNumber] = useState("");
  const [form, setForm] = useState({ name: "", description: "", tags: "", asset_account: "", credit_account: "", acquisition_date: today, acquisition_cost: "", non_depreciable: false, useful_life_years: 4, depreciation_account: "6-60200", accumulated_account: "", initial_accum: "", initial_accum_date: today });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/assets/next-number").then((r) => setNextNumber(r.number)).catch(() => {}); }, []);
  const byCat = (cats) => accounts.filter((a) => cats.includes(a.category));
  const fixedAccounts = byCat(["Fixed Assets"]);
  const creditAccounts = byCat(["Cash & Bank", "Other Current Liabilities", "Long Term Liabilities", "Equity"]);
  const expenseAccounts = byCat(["Expenses", "Other Expenses", "Cost of Sales"]);
  const accumAccounts = byCat(["Depreciation & Amortization"]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const rate = form.useful_life_years > 0 ? Math.round((100 / form.useful_life_years) * 100) / 100 : 0;
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = { ...form, acquisition_cost: Number(form.acquisition_cost) || 0, useful_life_years: Number(form.useful_life_years) || 4, initial_accum: Number(form.initial_accum) || 0 };
      if (!payload.initial_accum) delete payload.initial_accum_date;
      const r = await api("/assets", { method: "POST", body: JSON.stringify(payload) });
      onSaved(r);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const valid = form.name.trim() && form.asset_account && form.credit_account && form.acquisition_date && (form.non_depreciable || (form.depreciation_account && form.accumulated_account && Number(form.useful_life_years) > 0));
  return (
    <div className="modal-overlay" data-testid="asset-form-modal">
      <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel" data-testid="asset-form">
          <div className="section-head">
            <div><p className="eyebrow">ASET TETAP</p><h2>Penyimpanan Aset Baru</h2></div>
            <button data-testid="asset-form-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          {error && <div className="journal-balance bad" data-testid="asset-form-error">{error}</div>}
          <div className="drawer-section-title">Detail Aset</div>
          <div className="form-grid">
            <label>Nama aset *<input data-testid="asset-field-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
            <label>Tanggal akuisisi *<input data-testid="asset-field-date" type="date" value={form.acquisition_date} onChange={(e) => set("acquisition_date", e.target.value)} /></label>
            <label>Nomor aset<input data-testid="asset-field-number" value={nextNumber} disabled /></label>
            <label>Biaya akuisisi (IDR)<input data-testid="asset-field-cost" type="number" min="0" placeholder="0" value={form.acquisition_cost} onChange={(e) => set("acquisition_cost", e.target.value)} /></label>
            <label>Akun aset tetap *
              <select data-testid="asset-field-account" value={form.asset_account} onChange={(e) => set("asset_account", e.target.value)}>
                <option value="">Pilih akun...</option>
                {fixedAccounts.map((a) => <option key={a.code} value={a.code}>({a.code}) {a.name}</option>)}
              </select>
            </label>
            <label>Akun dikreditkan *
              <select data-testid="asset-field-credit" value={form.credit_account} onChange={(e) => set("credit_account", e.target.value)}>
                <option value="">Pilih akun...</option>
                {creditAccounts.map((a) => <option key={a.code} value={a.code}>({a.code}) {a.name}</option>)}
              </select>
            </label>
            <label>Deskripsi<input data-testid="asset-field-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsional" /></label>
            <label>Tags<input data-testid="asset-field-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Opsional" /></label>
          </div>
          <div className="drawer-section-title">Penyusutan</div>
          <label className="asset-check">
            <input data-testid="asset-field-nondep" type="checkbox" checked={form.non_depreciable} onChange={(e) => set("non_depreciable", e.target.checked)} />
            Aset non-depresiasi (mis. tanah — tidak disusutkan)
          </label>
          {!form.non_depreciable && (
            <div className="form-grid">
              <label>Metode<input value="Garis Lurus (Straight Line)" disabled /></label>
              <label>Akun penyusutan *
                <select data-testid="asset-field-depacc" value={form.depreciation_account} onChange={(e) => set("depreciation_account", e.target.value)}>
                  <option value="">Pilih akun...</option>
                  {expenseAccounts.map((a) => <option key={a.code} value={a.code}>({a.code}) {a.name}</option>)}
                </select>
              </label>
              <label>Masa manfaat (tahun) *<input data-testid="asset-field-life" type="number" min="0.5" step="0.5" value={form.useful_life_years} onChange={(e) => set("useful_life_years", e.target.value)} /></label>
              <label>Akun akumulasi penyusutan *
                <select data-testid="asset-field-accacc" value={form.accumulated_account} onChange={(e) => set("accumulated_account", e.target.value)}>
                  <option value="">Pilih akun...</option>
                  {accumAccounts.map((a) => <option key={a.code} value={a.code}>({a.code}) {a.name}</option>)}
                </select>
              </label>
              <label>Nilai/tahun (%)<input data-testid="asset-field-rate" value={rate} disabled /></label>
              <label>Akumulasi penyusutan awal (aset lama)<input data-testid="asset-field-initaccum" type="number" min="0" placeholder="0" value={form.initial_accum} onChange={(e) => set("initial_accum", e.target.value)} /></label>
              {Number(form.initial_accum) > 0 && (
                <label>Pada tanggal<input data-testid="asset-field-initdate" type="date" value={form.initial_accum_date} onChange={(e) => set("initial_accum_date", e.target.value)} /></label>
              )}
            </div>
          )}
          <div className="form-actions">
            <button data-testid="asset-save-button" className="primary-btn" disabled={!valid || busy} onClick={submit}>{busy ? "Menyimpan..." : "Buat Aset"}</button>
            <button data-testid="asset-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetDrawer({ asset, amap, deps, onClose, onActivate, onDispose, onDelete, onRename }) {
  useEscape(onClose);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: asset.name, description: asset.description || "", tags: asset.tags || "" });
  const statusCls = { PENDING: "pending", ACTIVE: "active", DISPOSED: "disposed" }[asset.status];
  return (
    <>
    <div className="drawer-backdrop" data-testid="asset-drawer-backdrop" onClick={onClose} />
    <div className="detail-drawer v2" data-testid="asset-detail-drawer">
      <div className="drawer-hero">
        <div className="drawer-hero-top">
          <div>
            <p className="eyebrow">ASET TETAP</p>
            <h2>({asset.number}) {asset.name}</h2>
            <p className="drawer-hero-party">({asset.asset_account}) {amap[asset.asset_account] || ""}</p>
          </div>
          <button data-testid="asset-close-detail" className="icon-btn light" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-hero-meta">
          <span className={`status ${statusCls}`}>{STATUS_LABEL[asset.status]}</span>
          <span>Akuisisi {fmtDate(asset.acquisition_date)}</span>
        </div>
        <div className="drawer-hero-stats">
          <div><small>Biaya Akuisisi</small><b>{money(asset.acquisition_cost)}</b></div>
          <div><small>Akumulasi Penyusutan</small><b>{money(asset.accum_total || 0)}</b></div>
          <div className="ok"><small>Nilai Buku</small><b>{money(asset.book_value)}</b></div>
        </div>
      </div>
      <div className="drawer-body">
        {asset.status === "DISPOSED" && (
          <p className="muted ju-auto-note" data-testid="asset-disposed-note">Dilepas pada <b>{fmtDate(asset.disposal_date)}</b> dengan harga jual <b>{money(asset.disposal_proceeds || 0)}</b> — {asset.disposal_gain_loss >= 0 ? "laba" : "rugi"} pelepasan <b>{money(Math.abs(asset.disposal_gain_loss || 0))}</b>.</p>
        )}
        <div className="drawer-section-title">Informasi Aset</div>
        {!edit ? (
          <div className="detail-fields v2">
            <div><small>Nomor</small><strong>{asset.number}</strong></div>
            <div><small>Akun Dikreditkan</small><strong>({asset.credit_account}) {amap[asset.credit_account] || ""}</strong></div>
            {!asset.non_depreciable && <div><small>Metode</small><strong>Garis Lurus · {asset.rate_percent}%/tahun</strong></div>}
            {!asset.non_depreciable && <div><small>Masa Manfaat</small><strong>{asset.useful_life_years} tahun</strong></div>}
            {!asset.non_depreciable && <div><small>Akun Penyusutan</small><strong>({asset.depreciation_account}) {amap[asset.depreciation_account] || ""}</strong></div>}
            {!asset.non_depreciable && <div><small>Akun Akumulasi</small><strong>({asset.accumulated_account}) {amap[asset.accumulated_account] || ""}</strong></div>}
            {asset.non_depreciable && <div><small>Penyusutan</small><strong>Non-depresiasi</strong></div>}
            {asset.pending_months > 0 && <div><small>Penyusutan Tertunda</small><strong>{asset.pending_months} bulan × {money(asset.monthly_amount)}</strong></div>}
            {asset.description && <div><small>Deskripsi</small><strong>{asset.description}</strong></div>}
            {asset.tags && <div><small>Tags</small><strong>{asset.tags}</strong></div>}
          </div>
        ) : (
          <div className="form-grid">
            <label>Nama aset<input data-testid="asset-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Tags<input data-testid="asset-edit-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></label>
            <label>Deskripsi<input data-testid="asset-edit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
          </div>
        )}
        {deps.length > 0 && (
          <div className="doc-lines v2">
            <div className="drawer-section-title">Riwayat Penyusutan</div>
            <table className="doc-line-table v2">
              <thead><tr><th>PERIODE</th><th>JURNAL</th><th className="right">JUMLAH</th></tr></thead>
              <tbody>
                {deps.map((d) => <tr key={d.id}><td>{fmtPeriod(d.period)}</td><td className="mono">{d.journal_number}</td><td className="right mono">{money(d.amount)}</td></tr>)}
              </tbody>
            </table>
          </div>
        )}
        <div className="drawer-section-title">Tindakan</div>
        <div className="drawer-actions v2">
          {edit ? (
            <>
              <button data-testid="asset-edit-save" className="primary-btn" disabled={!form.name.trim()} onClick={() => { onRename(form); setEdit(false); }}>Simpan</button>
              <button className="secondary-btn" onClick={() => setEdit(false)}>Batal</button>
            </>
          ) : (
            <>
              {asset.status === "PENDING" && <button data-testid="asset-drawer-activate" className="primary-btn" onClick={onActivate}>Aktifkan Aset</button>}
              {asset.status === "ACTIVE" && <button data-testid="asset-dispose-button" className="primary-btn" onClick={onDispose}>Jual / Lepas</button>}
              {asset.status !== "DISPOSED" && <button data-testid="asset-edit-button" className="secondary-btn" onClick={() => setEdit(true)}>Ubah</button>}
              {asset.status !== "DISPOSED" && <button data-testid="asset-delete-button" className="danger-btn" onClick={onDelete}>Hapus</button>}
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

function DisposeModal({ asset, accounts, onClose, onDone }) {
  const cashAccounts = accounts.filter((a) => a.category === "Cash & Bank");
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), proceeds: "", cash_account: cashAccounts[0]?.code || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const gain = (Number(form.proceeds) || 0) - asset.book_value;
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const r = await api(`/assets/${asset.id}/dispose`, { method: "POST", body: JSON.stringify({ ...form, proceeds: Number(form.proceeds) || 0 }) });
      onDone(r);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div className="modal-overlay" data-testid="asset-dispose-modal">
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head"><div><p className="eyebrow">PELEPASAN ASET</p><h2>Jual / Lepas Aset</h2></div><button data-testid="asset-dispose-close" className="icon-btn" onClick={onClose}><X size={18} /></button></div>
          {error && <div className="journal-balance bad" data-testid="asset-dispose-error">{error}</div>}
          <p className="muted delete-warning">({asset.number}) {asset.name} — nilai buku saat ini <b>{money(asset.book_value)}</b>. Jurnal pelepasan (aset, akumulasi, laba/rugi) diposting otomatis.</p>
          <div className="form-grid">
            <label>Tanggal pelepasan *<input data-testid="asset-dispose-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
            <label>Harga jual (IDR)<input data-testid="asset-dispose-proceeds" type="number" min="0" placeholder="0 jika dibuang" value={form.proceeds} onChange={(e) => setForm({ ...form, proceeds: e.target.value })} /></label>
            <label>Akun kas/bank penerima *
              <select data-testid="asset-dispose-cash" value={form.cash_account} onChange={(e) => setForm({ ...form, cash_account: e.target.value })}>
                {cashAccounts.map((a) => <option key={a.code} value={a.code}>({a.code}) {a.name}</option>)}
              </select>
            </label>
          </div>
          <div className="doc-totals v2 bom-totals" data-testid="asset-dispose-preview">
            <div><span>Nilai buku</span><b>{money(asset.book_value)}</b></div>
            <div><span>Harga jual</span><b>{money(Number(form.proceeds) || 0)}</b></div>
            <div className="grand"><span>{gain >= 0 ? "Laba" : "Rugi"} pelepasan</span><b className={gain >= 0 ? "mv-in" : "mv-out"}>{money(Math.abs(gain))}</b></div>
          </div>
          <div className="form-actions">
            <button data-testid="asset-dispose-confirm" className="danger-btn" disabled={!form.cash_account || busy} onClick={submit}>{busy ? "Memproses..." : "Lepas Aset"}</button>
            <button data-testid="asset-dispose-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}
